import { useState } from "react";

import { ThemedDropdown } from "@vega-core/components/Form/Controls/Dropdown/ThemedDropdown/ThemedDropdown";
import { InputControl } from "@vega-core/components/Form/Controls/Input/InputControl";
import { type OptionType } from "@vega-core/models/options";
import { m } from "@vega-core/paraglide/messages";

export const MAX_TTL_DAYS = 365;
export const DEFAULT_TTL_DAYS = 365;
const TTL_PRESETS = [30, 90, 180, 365];
const CUSTOM = "custom";

export const isValidTtl = (days: number) =>
  Number.isInteger(days) && days >= 1 && days <= MAX_TTL_DAYS;

type TtlSelectProps = {
  value: number;
  onChange: (days: number) => void;
};

export const TtlSelect = ({ value, onChange }: TtlSelectProps) => {
  const [mode, setMode] = useState<string>(
    TTL_PRESETS.includes(value) ? String(value) : CUSTOM,
  );
  const [customValue, setCustomValue] = useState(String(value));

  const options: OptionType[] = [
    ...TTL_PRESETS.map((days) => ({
      label: m.settings_reportingApiKeys_generate_expiryDays({ days }),
      value: String(days),
    })),
    {
      label: m.settings_reportingApiKeys_generate_expiryCustom(),
      value: CUSTOM,
    },
  ];

  const customDays = Number(customValue);
  const customError =
    mode === CUSTOM && !isValidTtl(customDays)
      ? m.settings_reportingApiKeys_generate_expiryInvalid()
      : undefined;

  return (
    <div className="flex flex-col gap-2">
      <ThemedDropdown
        isClearable={false}
        label={m.settings_reportingApiKeys_generate_expiry()}
        name="reporting-api-keys-ttl"
        onChange={(option) => {
          if (!option) return;
          setMode(option.value);

          if (option.value !== CUSTOM) {
            onChange(parseInt(option.value, 10));
          } else if (isValidTtl(customDays)) {
            onChange(customDays);
          }
        }}
        options={options}
        value={mode}
      />
      {mode === CUSTOM ? (
        <InputControl
          error={customError}
          label={m.settings_reportingApiKeys_generate_expiryCustomLabel()}
          name="reporting-api-keys-ttl-custom"
          onChange={(event) => {
            setCustomValue(event.target.value);
            const days = Number(event.target.value);

            if (isValidTtl(days)) {
              onChange(days);
            }
          }}
          type="number"
          value={customValue}
          withBorder
        />
      ) : null}
    </div>
  );
};
